import {
  ControlModeTemplateFieldsType,
  ControlModeTemplateSettings,
} from '@src/types/ControlModes';
import { FormValue } from '../context/types';

/**
 * Validates all fields in the given settings
 */
export const validateFields = (
  settings: ControlModeTemplateSettings[],
  formValues: Record<string, FormValue>,
): { errors: Record<string, string>; hasErrors: boolean } => {
  const errors: Record<string, string> = {};
  let hasErrors = false;

  // Validate all fields in all settings
  if (settings) {
    settings.forEach((setting) => {
      // Check if setting has fields
      if (!setting.fields || setting.fields.length === 0) {
        errors[`setting_${setting.controllerField}`] =
          `Setting "${setting.uiLabel}" must have at least one field`;
        hasErrors = true;
        return;
      }

      setting.fields.forEach((field) => {
        const fieldValue = formValues[field.controllerField];
        let fieldHasError = false;

        // Check if the field should be considered required
        // A field is considered required if it doesn't have a defaultValue
        const isFieldRequired = field.defaultValue === undefined;

        // Only validate required fields or fields that have a value
        if (isFieldRequired || fieldValue !== undefined) {
          // Handle different field types appropriately
          if (field.type === ControlModeTemplateFieldsType.BOOLEAN) {
            // For boolean fields, undefined and null are invalid (false is a valid value)
            if (
              isFieldRequired &&
              (fieldValue === undefined || fieldValue === null)
            ) {
              errors[field.controllerField] = 'This field is required';
              hasErrors = true;
              fieldHasError = true;
            }
          } else if (field.type === ControlModeTemplateFieldsType.NUMBER) {
            // For number fields, undefined and empty string are invalid (0 is a valid value)
            if (
              isFieldRequired &&
              (fieldValue === undefined || fieldValue === '')
            ) {
              errors[field.controllerField] = 'This field is required';
              hasErrors = true;
              fieldHasError = true;
            }
          } else {
            // For string and select fields, undefined and empty string are invalid
            if (
              isFieldRequired &&
              (fieldValue === undefined || fieldValue === '')
            ) {
              errors[field.controllerField] = 'This field is required';
              hasErrors = true;
              fieldHasError = true;
            }
          }
        }

        // Type-specific validation - only run if the field has a value
        if (!fieldHasError && fieldValue !== undefined && fieldValue !== '') {
          switch (field.type) {
            case ControlModeTemplateFieldsType.NUMBER:
              // Validate number range if specified
              if (field.range) {
                const numValue = Number(fieldValue);
                const min = field.range.min;
                const max = field.range.max;

                if (
                  min !== undefined &&
                  max !== undefined &&
                  (numValue < min || numValue > max)
                ) {
                  errors[field.controllerField] =
                    `Value must be between ${min} and ${max}`;
                  hasErrors = true;
                } else if (min !== undefined && numValue < min) {
                  errors[field.controllerField] =
                    `Value must be greater than or equal to ${min}`;
                  hasErrors = true;
                } else if (max !== undefined && numValue > max) {
                  errors[field.controllerField] =
                    `Value must be less than or equal to ${max}`;
                  hasErrors = true;
                }
              }
              break;
            case ControlModeTemplateFieldsType.SELECT:
              // Validate that value is in the list options
              if (
                field.listOptions &&
                fieldValue !== undefined &&
                fieldValue !== '' &&
                !field.listOptions.includes(String(fieldValue))
              ) {
                errors[field.controllerField] = 'Invalid selection';
                hasErrors = true;
              }
              break;
            case ControlModeTemplateFieldsType.BOOLEAN:
              // No additional validation needed for boolean fields
              break;
          }
        }
      });
    });
  }

  return { errors, hasErrors };
};

/**
 * Validates a new field being added or edited
 */
export const validateNewField = (
  fieldData: {
    uiLabel: string;
    controllerField: string;
    type: ControlModeTemplateFieldsType;
    defaultValue: string | number | boolean | null;
    range?: { min?: number; max?: number };
  },
  parsedOptions: string[],
): { errors: Record<string, string>; hasErrors: boolean } => {
  const errors: Record<string, string> = {};
  let hasErrors = false;

  // Validate required fields
  if (!fieldData.uiLabel) {
    errors['uiLabel'] = 'Field Label is required';
    hasErrors = true;
  }

  if (!fieldData.controllerField) {
    errors['controllerField'] = 'Controller Field is required';
    hasErrors = true;
  }

  // Type-specific validation
  if (fieldData.type === ControlModeTemplateFieldsType.SELECT) {
    // For SELECT type, validate that parsedOptions has at least one option
    if (parsedOptions.length === 0) {
      errors['listOptions'] = 'At least one option is required';
      hasErrors = true;
    }

    // If default value is provided, validate that it's in the parsed options
    if (fieldData.defaultValue && parsedOptions.length > 0) {
      const defaultValueStr = String(fieldData.defaultValue);
      if (!parsedOptions.includes(defaultValueStr)) {
        errors['defaultValue'] =
          'Default value must be one of the list options';
        hasErrors = true;
      }
    }
  } else if (fieldData.type === ControlModeTemplateFieldsType.NUMBER) {
    // For NUMBER type, validate that default value is within range if provided
    if (fieldData.defaultValue) {
      const numValue = Number(fieldData.defaultValue);
      if (isNaN(numValue)) {
        errors['defaultValue'] = 'Default value must be a number';
        hasErrors = true;
      } else if (fieldData.range) {
        const min = fieldData.range.min;
        const max = fieldData.range.max;

        if (
          min !== undefined &&
          max !== undefined &&
          (numValue < min || numValue > max)
        ) {
          errors['defaultValue'] =
            `Default value must be between ${min} and ${max}`;
          hasErrors = true;
        } else if (min !== undefined && numValue < min) {
          errors['defaultValue'] =
            `Default value must be greater than or equal to ${min}`;
          hasErrors = true;
        } else if (max !== undefined && numValue > max) {
          errors['defaultValue'] =
            `Default value must be less than or equal to ${max}`;
          hasErrors = true;
        }
      }
    }
  } else if (fieldData.type === ControlModeTemplateFieldsType.BOOLEAN) {
    // For BOOLEAN type, validate that default value is true or false
    if (fieldData.defaultValue !== undefined && fieldData.defaultValue !== '') {
      const defaultValueStr = String(fieldData.defaultValue).toLowerCase();
      if (
        defaultValueStr !== 'true' &&
        defaultValueStr !== 'false' &&
        fieldData.defaultValue !== true &&
        fieldData.defaultValue !== false
      ) {
        errors['defaultValue'] = 'Default value must be true or false';
        hasErrors = true;
      }
    }
  }

  return { errors, hasErrors };
};
